/**
 * Часовете на ВСИЧКИ деца на семейството — за календара на родителя.
 *
 * 🔴 Пътят е СЪРВЪРЕН по необходимост, не за удобство. Членството на детето в
 * клас живее в `class.childIds`, а `firestore.rules` дава на родителя само `get`
 * по класовете, НЕ `list` (изброяването би разкрило всички деца на училището).
 * Тоест от клиента класът на детето не може да бъде намерен и `getChildSchedule`
 * е единствената врата.
 *
 * ⚠️ Едно извикване на дете. Мерено решение (задача 19, Б): при 72% от
 * семействата това е ЕДНО извикване, при 25.5% — две. Общо callable за цялото
 * семейство е оправдано само ако числото го поиска, и тогава е свой кръг.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebase';
import type {
  GetChildScheduleRequest,
  GetChildScheduleResponse,
  TeacherScheduleSlotView,
} from '@shared/types';

export interface ChildrenCalendarSlots {
  /** Слотовете по дете. Липсващ ключ = още се зарежда; празен масив = няма клас. */
  byChildId: Record<string, TeacherScheduleSlotView[]>;
  loading: boolean;
}

export function useChildrenCalendarSlots(childIds: string[]): ChildrenCalendarSlots {
  const [byChildId, setByChildId] = useState<Record<string, TeacherScheduleSlotView[]>>({});
  const [loading, setLoading] = useState(false);

  /**
   * 🔴 Зависимостта е НИЗ, не масивът.
   *
   * `useChildren` връща нов масив при всяко зареждане; подаден пряко, той пуска
   * ефекта в кръг — същият дефект, който админският календар вече е платил с
   * обекта от `onSnapshot`. Ключът се мени само когато се промени съставът.
   */
  const key = childIds.join('|');

  useEffect(() => {
    const ids = key ? key.split('|') : [];
    if (ids.length === 0) {
      setByChildId({});
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const fn = httpsCallable<GetChildScheduleRequest, GetChildScheduleResponse>(
      functions,
      'getChildSchedule',
    );

    Promise.all(
      ids.map((childId) =>
        fn({ childId })
          .then((res) => [childId, res.data.calendarSlots ?? []] as const)
          // ⚠️ Отказът на ЕДНО дете не бива да изпразва календара на останалите.
          // Празен масив значи „няма какво да се покаже за него" — легендата го
          // отбелязва, вместо детето да изчезне.
          .catch(() => [childId, [] as TeacherScheduleSlotView[]] as const),
      ),
    ).then((pairs) => {
      if (cancelled) return;
      setByChildId(Object.fromEntries(pairs));
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [key]);

  return { byChildId, loading };
}
